import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Download, GitCompareArrows } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const COLORS = ["#f59e0b", "#3b82f6", "#10b981", "#ef4444", "#a855f7", "#14b8a6", "#f97316", "#ec4899", "#84cc16", "#06b6d4"];
const TT = { contentStyle: { background: "#09090b", border: "1px solid #27272a", fontSize: 12, borderRadius: 8 }, labelStyle: { color: "#e4e4e7", fontWeight: 600 }, itemStyle: { color: "#d4d4d8" } };
const AX = { tick: { fontSize: 10, fill: "#71717a" }, tickLine: false, axisLine: false };
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const slug = (s) => s.replace(/[^a-z0-9]/gi, "-").toLowerCase();

const csvDownload = (filename, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
};

export default function StoreCompareChart({ range, fidelityOnly, stores, weekdays = [], hours = "" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [metric, setMetric] = useState("total_eur");
  const [hidden, setHidden] = useState([]);
  const reqId = useRef(0);
  const key = `${range.date_from}|${range.date_to}|${fidelityOnly}|${stores.join("|")}|${weekdays.join(",")}|${hours}`;

  useEffect(() => {
    const id = ++reqId.current;
    setBusy(true); setError(null);
    api.get("/elastic/analytics/compare", { params: { ...range, fidelity_only: fidelityOnly, stores: stores.length ? stores.join("|") : undefined, weekdays: weekdays.length && weekdays.length < 7 ? weekdays.join(",") : undefined, hours: hours || undefined } })
      .then((r) => { if (id === reqId.current) { setData(r.data); setHidden([]); } })
      .catch((e) => { if (id === reqId.current) setError(formatApiError(e)); })
      .finally(() => { if (id === reqId.current) setBusy(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (error) return <div className="card-elev p-4 text-sm text-red-400" data-testid="crm-compare-error">{error}</div>;
  if (!data) return <div className="card-elev p-4 text-xs text-zinc-500" data-testid="crm-compare-loading">Confronto sedi: caricamento…</div>;
  if (stores.length === 1) return null;
  if (data.stores.length < 2) {
    return stores.length >= 2 ? <div className="card-elev p-4 text-xs text-zinc-500" data-testid="crm-compare-nodata">Confronto sedi: solo {data.stores.length} delle {stores.length} sedi selezionate ha scontrini nel periodo — servono almeno 2 sedi con dati.</div> : null;
  }

  const names = data.stores.map((s) => s.name);
  const rows = data.days.map((d) => ({ date: d.date, label: d.date.slice(5), ...Object.fromEntries(names.map((n) => [n, d[metric]?.[n] ?? 0])) }));
  const toggle = (n) => setHidden((h) => (h.includes(n) ? h.filter((x) => x !== n) : [...h, n]));
  const isEur = metric === "total_eur";
  const exportCsv = () => csvDownload(`crm_confronto_sedi_${metric}.csv`, ["Data", ...names], rows.map((r) => [r.date, ...names.map((n) => r[n])]));

  return (
    <section className="card-elev p-4 space-y-3" data-testid="crm-compare">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-heading font-bold text-base flex items-center gap-2"><GitCompareArrows className="h-4 w-4 text-[color:var(--paprika)]" /> Confronto sedi giorno per giorno</h2>
          <p className="text-[11px] text-zinc-500" data-testid="crm-compare-subtitle">
            {data.auto_top ? `Le ${names.length} sedi con più incasso nel periodo (seleziona i punti vendita nel filtro per confrontarne altri).` : `${names.length} sedi selezionate nel filtro.${data.requested > names.length ? ` ${data.requested - names.length} senza scontrini nel periodo.` : ""}`}
            {busy ? " Aggiornamento…" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-zinc-800 overflow-hidden text-xs" data-testid="crm-compare-metric">
            {[["total_eur", "Incasso"], ["receipts", "Scontrini"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMetric(k)} data-testid={`crm-compare-metric-${k}`}
                className={`px-3 h-8 ${metric === k ? "bg-[color:var(--paprika)] text-white" : "bg-zinc-950 text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={exportCsv} data-testid="crm-compare-csv"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
        </div>
      </div>
      <div className="h-72" data-testid="crm-compare-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 4, right: 8, left: -4, bottom: 0 }}>
            <CartesianGrid stroke="#27272a" vertical={false} />
            <XAxis dataKey="label" {...AX} minTickGap={24} />
            <YAxis {...AX} tickFormatter={(v) => (isEur ? `€${Math.round(v).toLocaleString("it-IT")}` : v.toLocaleString("it-IT"))} width={64} />
            <Tooltip {...TT} formatter={(v, n) => [isEur ? eur(v) : Number(v).toLocaleString("it-IT"), n]} labelFormatter={(l, p) => p?.[0]?.payload?.date || l} />
            {names.map((n, i) => (
              <Line key={n} type="linear" dataKey={n} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} hide={hidden.includes(n)} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="crm-compare-legend">
        {data.stores.map((s, i) => (
          <button key={s.name} type="button" onClick={() => toggle(s.name)} data-testid={`crm-compare-store-${slug(s.name)}`}
            className={`text-[11px] px-2 py-1 rounded-md border border-zinc-800 flex items-center gap-2 ${hidden.includes(s.name) ? "opacity-40 line-through" : ""}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="truncate max-w-[200px]">{s.name}</span>
            <span className="font-mono text-zinc-400">{isEur ? eur(s.total_eur) : `${s.receipts.toLocaleString("it-IT")} sc.`}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
